'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { TrendingUp, ArrowRight, BarChart2 } from 'lucide-react';

const InvestmentCharts = dynamic(() => import('./InvestmentCharts'), { ssr: false });

const TOP_INVESTMENT_DISTRICTS = [
  { id: 'inv-konyaalti', district: 'Konyaaltı', city: 'Antalya', yield: '7.2', score: 91, change: '+18.4%', positive: true },
  { id: 'inv-alacati', district: 'Alaçatı', city: 'İzmir', yield: '8.1', score: 88, change: '+22.1%', positive: true },
  { id: 'inv-nilüfer', district: 'Nilüfer', city: 'Bursa', yield: '6.8', score: 84, change: '+11.3%', positive: true },
  { id: 'inv-kadikoy', district: 'Kadıköy', city: 'İstanbul', yield: '5.8', score: 82, change: '+12.4%', positive: true },
  { id: 'inv-cankaya', district: 'Çankaya', city: 'Ankara', yield: '6.1', score: 78, change: '+9.6%', positive: true },
  { id: 'inv-konak', district: 'Konak', city: 'İzmir', yield: '5.4', score: 72, change: '-2.3%', positive: false },
];

export default function InvestmentSection() {
  return (
    <section className="py-20 bg-muted">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: Districts Table */}
          <div>
            <p className="section-label mb-2">Yatırım Analizi</p>
            <h2 className="text-display font-bold text-foreground mb-2">
              En Yüksek Kira Getirili Bölgeler
            </h2>
            <p className="text-muted-foreground mb-6">Ağustos 2026 verilerine göre Türkiye&apos;nin en yüksek brüt kira getirisine sahip ilçeleri</p>

            <div className="card-elevated rounded-2xl overflow-hidden">
              <div className="grid grid-cols-5 px-4 py-3 bg-muted border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <span className="col-span-2">Bölge</span>
                <span className="text-center">Getiri</span>
                <span className="text-center">Skor</span>
                <span className="text-right">Değişim</span>
              </div>
              {TOP_INVESTMENT_DISTRICTS?.map((d, i) => (
                <Link key={d?.id} href={`/property-search?ilce=${d?.district?.toLowerCase()}`} className="block">
                  <div className="grid grid-cols-5 px-4 py-3.5 border-b border-border last:border-0 hover:bg-primary-light transition-colors group items-center">
                    <div className="col-span-2 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-md bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground flex-shrink-0">
                        {i + 1}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-foreground group-hover:text-primary">{d?.district}</p>
                        <p className="text-xs text-muted-foreground">{d?.city}</p>
                      </div>
                    </div>
                    <div className="text-center">
                      <span className="text-sm font-bold text-success tabular-nums">%{d?.yield}</span>
                    </div>
                    <div className="flex justify-center">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-md tabular-nums ${d?.score >= 85 ? 'bg-success-light text-success' : d?.score >= 75 ? 'bg-warning-light text-warning' : 'bg-muted text-muted-foreground'}`}>
                        {d?.score}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs font-bold flex items-center gap-0.5 justify-end tabular-nums ${d?.positive ? 'text-success' : 'text-danger'}`}>
                        <TrendingUp size={11} />
                        {d?.change}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">* Tüm değerler tahmini olup yatırım tavsiyesi niteliği taşımaz.</p>
              <Link href="/property-search?yatirim=true" className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                Tüm Analiz <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          {/* Right: Chart */}
          <div>
            <div className="card-elevated rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm font-semibold text-muted-foreground mb-0.5">Şehir Bazlı Kira Getirisi</p>
                  <h3 className="text-lg font-bold text-foreground">Brüt Getiri Karşılaştırması</h3>
                </div>
                <div className="w-9 h-9 rounded-xl bg-primary-light flex items-center justify-center">
                  <BarChart2 size={18} className="text-primary" />
                </div>
              </div>
              <InvestmentCharts />
              <p className="text-xs text-muted-foreground mt-4 text-center">Kaynak: Evora Piyasa Analizi, Ağustos 2026. Tahmin niteliğindedir.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}